import { supabase } from '../supabaseClient';

const normalizeProfile = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    auth_user_id: row.portal_user_id,
    first_name: row.first_name,
    last_name: row.last_name,
    email: row.primary_email,
    phone: row.primary_phone,
    nationality: row.nationality,
    passport_no: row.passport_no,
    created_at: row.created_at,
    updated_at: row.updated_at
  };
};

export const PortalProfileService = {
  async getClientProfile({ authUserId, email }) {
    try {
      const fields = `
        id,
        portal_user_id,
        first_name,
        last_name,
        primary_email,
        primary_phone,
        nationality,
        passport_no,
        created_at,
        updated_at
      `;

      let profile = null;
      if (authUserId) {
        const { data, error } = await supabase
          .from('clients')
          .select(fields)
          .eq('portal_user_id', authUserId)
          .maybeSingle();
        if (error) throw error;
        profile = data || null;
      }

      if (!profile && email) {
        const { data, error } = await supabase
          .from('clients')
          .select(fields)
          .ilike('primary_email', email)
          .maybeSingle();
        if (error) throw error;
        profile = data || null;
      }

      return { data: normalizeProfile(profile), error: null };
    } catch (error) {
      console.error('[PortalProfileService] getClientProfile failed', error);
      return { data: null, error };
    }
  },

  async updateClientProfile(clientId, updates = {}) {
    if (!clientId) return { data: null, error: new Error('Missing client id') };
    try {
      const payload = {
        first_name: updates.first_name ?? undefined,
        last_name: updates.last_name ?? undefined,
        primary_email: updates.email ?? undefined,
        primary_phone: updates.phone ?? undefined,
        updated_at: new Date().toISOString()
      };
      const { data, error } = await supabase
        .from('clients')
        .update(payload)
        .eq('id', clientId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[PortalProfileService] updateClientProfile failed', error);
      return { data: null, error };
    }
  },

  async upsertAddress(clientId, address = {}) {
    if (!clientId) return { data: null, error: new Error('Missing client id') };
    try {
      const payload = {
        client_id: clientId,
        line1: address.line1 || '',
        line2: address.line2 || null,
        city: address.city || null,
        state: address.state || null,
        postal_code: address.postalCode || null,
        country: address.country || null,
        type: address.type || 'primary',
        updated_at: new Date().toISOString()
      };

      if (address.id) {
        const { data, error } = await supabase
          .from('client_addresses')
          .update(payload)
          .eq('id', address.id)
          .select()
          .single();
        if (error) throw error;
        return { data, error: null };
      }

      const { data, error } = await supabase
        .from('client_addresses')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[PortalProfileService] upsertAddress failed', error);
      return { data: null, error };
    }
  },

  async upsertContact(clientId, contact = {}) {
    if (!clientId) return { data: null, error: new Error('Missing client id') };
    try {
      const payload = {
        client_id: clientId,
        name: contact.name || '',
        relationship: contact.relationship || null,
        email: contact.email || null,
        phone: contact.phone || null,
        type: contact.type || 'primary',
        updated_at: new Date().toISOString()
      };

      if (contact.id) {
        const { data, error } = await supabase
          .from('client_contacts')
          .update(payload)
          .eq('id', contact.id)
          .select()
          .single();
        if (error) throw error;
        return { data, error: null };
      }

      const { data, error } = await supabase
        .from('client_contacts')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[PortalProfileService] upsertContact failed', error);
      return { data: null, error };
    }
  },

  async updateConsents(clientId, consents = {}) {
    if (!clientId) return { data: null, error: new Error('Missing client id') };
    try {
      const { data, error } = await supabase
        .from('client_consents')
        .upsert({
          client_id: clientId,
          marketing_opt_in: !!consents.marketingOptIn,
          data_processing_opt_in: !!consents.dataProcessingOptIn,
          updated_at: new Date().toISOString()
        }, { onConflict: 'client_id' })
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[PortalProfileService] updateConsents failed', error);
      return { data: null, error };
    }
  },

  // Education
  async listEducation(clientId) {
    if (!clientId) return { data: [], error: new Error('Missing client id') };
    try {
      const { data, error } = await supabase
        .from('wizard_submissions')
        .select('metadata')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      // Note: Currently education is stored in wizard_submissions metadata. 
      // Ideally this should be normalized to a client_education table.
      // For now, we'll extract from the latest submission if available.
      const education = data?.[0]?.metadata?.education || [];
      return { data: education, error: null };
    } catch (error) {
      console.error('[PortalProfileService] listEducation failed', error);
      return { data: [], error };
    }
  },

  // Experience
  async listExperience(clientId) {
    if (!clientId) return { data: [], error: new Error('Missing client id') };
    try {
      const { data, error } = await supabase
        .from('wizard_submissions')
        .select('metadata')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      const experience = data?.[0]?.metadata?.work_experience || [];
      return { data: experience, error: null };
    } catch (error) {
      console.error('[PortalProfileService] listExperience failed', error);
      return { data: [], error };
    }
  },

  // Languages
  async listLanguages(clientId) {
    if (!clientId) return { data: [], error: new Error('Missing client id') };
    try {
      const { data, error } = await supabase
        .from('wizard_submissions')
        .select('metadata')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      const languages = data?.[0]?.metadata?.languages || [];
      return { data: languages, error: null };
    } catch (error) {
      console.error('[PortalProfileService] listLanguages failed', error);
      return { data: [], error };
    }
  },

  // Family - Dependents
  async listDependents(clientId) {
    if (!clientId) return { data: [], error: new Error('Missing client id') };
    try {
      const { data, error } = await supabase.from('client_dependents').select('*').eq('client_id', clientId).order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('[PortalProfileService] listDependents failed', error);
      return { data: [], error };
    }
  },

  async upsertDependent(clientId, dependent = {}) {
    if (!clientId) return { data: null, error: new Error('Missing client id') };
    try {
      const payload = {
        client_id: clientId,
        name: dependent.name,
        date_of_birth: dependent.date_of_birth || null,
        relationship: dependent.relationship || null,
        passport_no: dependent.passport_no || null,
        updated_at: new Date().toISOString()
      };
      if (dependent.id) {
        const { data, error } = await supabase.from('client_dependents').update(payload).eq('id', dependent.id).select().single();
        if (error) throw error;
        return { data, error: null };
      }
      const { data, error } = await supabase.from('client_dependents').insert(payload).select().single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[PortalProfileService] upsertDependent failed', error);
      return { data: null, error };
    }
  },

  async deleteDependent(id) {
    if (!id) return { error: new Error('Missing id') };
    try {
      const { error } = await supabase.from('client_dependents').delete().eq('id', id);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Family - Guardians
  async listGuardians(clientId) {
    if (!clientId) return { data: [], error: new Error('Missing client id') };
    try {
      const { data, error } = await supabase.from('client_guardians').select('*').eq('client_id', clientId).order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('[PortalProfileService] listGuardians failed', error);
      return { data: [], error };
    }
  },

  async upsertGuardian(clientId, guardian = {}) {
    if (!clientId) return { data: null, error: new Error('Missing client id') };
    try {
      const payload = {
        client_id: clientId,
        name: guardian.name,
        relationship: guardian.relationship || null,
        phone: guardian.phone || null,
        email: guardian.email || null,
        is_primary: !!guardian.is_primary,
        updated_at: new Date().toISOString()
      };
      if (guardian.id) {
        const { data, error } = await supabase.from('client_guardians').update(payload).eq('id', guardian.id).select().single();
        if (error) throw error;
        return { data, error: null };
      }
      const { data, error } = await supabase.from('client_guardians').insert(payload).select().single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[PortalProfileService] upsertGuardian failed', error);
      return { data: null, error };
    }
  },

  async deleteGuardian(id) {
    if (!id) return { error: new Error('Missing id') };
    try {
      const { error } = await supabase.from('client_guardians').delete().eq('id', id);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Family - Siblings
  async listSiblings(clientId) {
    if (!clientId) return { data: [], error: new Error('Missing client id') };
    try {
      const { data, error } = await supabase.from('client_siblings').select('*').eq('client_id', clientId).order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('[PortalProfileService] listSiblings failed', error);
      return { data: [], error };
    }
  },

  async upsertSibling(clientId, sibling = {}) {
    if (!clientId) return { data: null, error: new Error('Missing client id') };
    try {
      const payload = {
        client_id: clientId,
        name: sibling.name,
        date_of_birth: sibling.date_of_birth || null,
        country_of_residence: sibling.country_of_residence || null,
        updated_at: new Date().toISOString()
      };
      if (sibling.id) {
        const { data, error } = await supabase.from('client_siblings').update(payload).eq('id', sibling.id).select().single();
        if (error) throw error;
        return { data, error: null };
      }
      const { data, error } = await supabase.from('client_siblings').insert(payload).select().single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[PortalProfileService] upsertSibling failed', error);
      return { data: null, error };
    }
  },

  async deleteSibling(id) {
    if (!id) return { error: new Error('Missing id') };
    try {
      const { error } = await supabase.from('client_siblings').delete().eq('id', id);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Visa Refusals
  async listVisaRefusals(clientId) {
    if (!clientId) return { data: [], error: new Error('Missing client id') };
    try {
      const { data, error } = await supabase.from('client_visa_refusals').select('*').eq('client_id', clientId).order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('[PortalProfileService] listVisaRefusals failed', error);
      return { data: [], error };
    }
  },

  async upsertVisaRefusal(clientId, refusal = {}) {
    if (!clientId) return { data: null, error: new Error('Missing client id') };
    try {
      const payload = {
        client_id: clientId,
        country: refusal.country,
        refusal_date: refusal.refusal_date || null,
        reason: refusal.reason || null,
        updated_at: new Date().toISOString()
      };
      if (refusal.id) {
        const { data, error } = await supabase.from('client_visa_refusals').update(payload).eq('id', refusal.id).select().single();
        if (error) throw error;
        return { data, error: null };
      }
      const { data, error } = await supabase.from('client_visa_refusals').insert(payload).select().single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[PortalProfileService] upsertVisaRefusal failed', error);
      return { data: null, error };
    }
  },

  async deleteVisaRefusal(id) {
    if (!id) return { error: new Error('Missing id') };
    try {
      const { error } = await supabase.from('client_visa_refusals').delete().eq('id', id);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  }
};
